import { ReactNode, useMemo } from "react";
import {
  ChatBubbleLeftRightIcon,
  PhoneArrowUpRightIcon,
  PhoneIcon,
} from "@heroicons/react/24/outline";

import { cn } from "../../utls/utils";
import Typography from "../typography";
import { DetailItem, DetailItemLayout } from "./DetailItem";

interface PhoneDetailItemActionProps {
  label: string;
  onClick: () => void;
  icon: ReactNode;
  "data-testid"?: string;
}

export interface PhoneDetailItemProps {
  /** Label displayed for the detail item. */
  label: string;
  /** Raw phone number string. Non-digit characters will be stripped automatically. */
  phoneNumber: string;
  /** Optional extension to append to the phone number. */
  extension?: string;
  /** Optional contact name rendered above the number. */
  contactName?: string;
  /** Helper text rendered next to the label. */
  helperText?: ReactNode;
  /** Custom icon to override the default phone glyph. */
  icon?: ReactNode;
  /** Layout style inherited from `DetailItem`. */
  layout?: DetailItemLayout;
  /** Additional class name for the wrapper. */
  className?: string;
  /** Called when the call button is pressed. Receives the normalized tel link value. */
  onCall?: (telHref: string) => void;
  /** Called when the message button is pressed. Receives the normalized sms link value. */
  onMessage?: (smsHref: string) => void;
  /** Render additional custom actions. */
  actions?: ReactNode;
  /** Whether to hide the built-in call action button. */
  hideCallAction?: boolean;
  /** Whether to hide the built-in message action button. */
  hideMessageAction?: boolean;
}

function buildTelHref(phoneNumber: string, extension?: string) {
  const digits = phoneNumber.replace(/[^0-9+]/g, "");
  const safeExtension = extension?.replace(/[^0-9]/g, "");
  if (!safeExtension) {
    return `tel:${digits}`;
  }

  return `tel:${digits};ext=${safeExtension}`;
}

function buildSmsHref(phoneNumber: string) {
  const digits = phoneNumber.replace(/[^0-9+]/g, "");
  return `sms:${digits}`;
}

function formatDisplayNumber(phoneNumber: string, extension?: string) {
  const digits = phoneNumber.replace(/[^0-9]/g, "");

  if (digits.length === 10) {
    const area = digits.slice(0, 3);
    const prefix = digits.slice(3, 6);
    const line = digits.slice(6);
    const formatted = `(${area}) ${prefix}-${line}`;
    return extension ? `${formatted} ext. ${extension}` : formatted;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    const area = digits.slice(1, 4);
    const prefix = digits.slice(4, 7);
    const line = digits.slice(7);
    const formatted = `+1 (${area}) ${prefix}-${line}`;
    return extension ? `${formatted} ext. ${extension}` : formatted;
  }

  return extension ? `${phoneNumber} ext. ${extension}` : phoneNumber;
}

function ActionButton({ label, onClick, icon, "data-testid": dataTestId }: PhoneDetailItemActionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={dataTestId}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border border-terracotta-300 px-3.5 py-1.5 text-sm font-medium", 
        "text-terracotta-700 transition hover:border-terracotta-400 hover:bg-terracotta-50", 
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-terracotta-300 focus-visible:ring-offset-2"
      )}
    >
      <span className="flex h-4 w-4 items-center justify-center text-current" aria-hidden>
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

export function PhoneDetailItem({
  label,
  phoneNumber,
  extension,
  contactName,
  helperText,
  icon,
  layout,
  className,
  onCall,
  onMessage,
  actions,
  hideCallAction = false,
  hideMessageAction = false,
}: PhoneDetailItemProps) {
  const telHref = useMemo(() => buildTelHref(phoneNumber, extension), [
    phoneNumber,
    extension,
  ]);
  const smsHref = useMemo(() => buildSmsHref(phoneNumber), [phoneNumber]);
  const displayNumber = useMemo(
    () => formatDisplayNumber(phoneNumber, extension),
    [phoneNumber, extension]
  );

  const resolvedIcon = icon ?? <PhoneIcon className="h-5 w-5" aria-hidden />;

  return (
    <DetailItem
      label={label}
      value={
        <div className="space-y-4">
          <div className="space-y-1">
            {contactName ? (
              <Typography variant="subtitle2" className="text-terracotta-700">
                {contactName}
              </Typography>
            ) : null}
            <Typography variant="body1" className="text-brown-800" data-testid="phone-number">
              {displayNumber}
            </Typography>
          </div>
          <div className="flex flex-wrap gap-2">
            {!hideCallAction ? (
              <ActionButton
                label="Call"
                icon={<PhoneArrowUpRightIcon className="h-4 w-4" />}
                onClick={() => {
                  if (onCall) {
                    onCall(telHref);
                  } else if (typeof window !== "undefined") {
                    window.open(telHref);
                  }
                }}
                data-testid="call-action"
              />
            ) : null}
            {!hideMessageAction ? (
              <ActionButton
                label="Text"
                icon={<ChatBubbleLeftRightIcon className="h-4 w-4" />}
                onClick={() => {
                  if (onMessage) {
                    onMessage(smsHref);
                  } else if (typeof window !== "undefined") {
                    window.open(smsHref);
                  }
                }}
                data-testid="message-action"
              />
            ) : null}
            {actions}
          </div>
        </div>
      }
      helperText={helperText}
      icon={resolvedIcon}
      layout={layout}
      className={className}
    />
  );
}

export default PhoneDetailItem;
